import React, {useEffect, useState, useMemo} from 'react';
import {connect} from 'react-redux';

import {
  Button,
  Grid,
  GridCol,
  Input,
  Select,
  SelectOption
} from '@flixbus/honeycomb-react';
import {Icon, IconDelete, IconOfferSolid} from '@flixbus/honeycomb-icons-react';
import {isEmpty, isNil} from 'ramda';
import {
  addPartnerDepot,
  deletePartnerDepot,
  resetPartner,
  setPartner,
  updatePartnerDepot
} from '../store/slice';
import {savePartner} from '../store/thunks';
import ContentLoader from '../../Common/ContentLoader';
import {confirm} from '../../Common/Confirm';
import {isBlank, isNotNilOrEmpty} from 'ramda-adjunct';
import CpcPanel from '../../Common/CpcPanel';
import LabelWithTooltip from '@/components/LabelWithTooltip/LabelWithTooltip.js';
import fieldInfoContent from '@/Pages/ProductionCost/constants/fieldInfoContent.js';
import ReadOnlyInput from '@/components/ReadOnlyInput/ReadOnlyInput';
import {BusPartnerStatus, BusPartnerSource} from '../store/types';
import {toCapitalized} from '@/Pages/Common/Utils';
import {useCountries} from '@/Pages/Countries/hooks/useCountries';

const PartnerPanel = ({
  partner,
  setPartner,
  resetPartner,
  savePartner,
  addPartnerDepot,
  updatePartnerDepot,
  deletePartnerDepot,
  onClose
}) => {
  const {countries} = useCountries();

  const [showPanel, setShowPanel] = useState(false);
  const [nameIsValid, setNameIsValid] = useState(null);
  const [countryIdIsValid, setCountryIdIsValid] = useState(null);
  const [depotsAreValid, setDepotsAreValid] = useState([]);
  const isEditPartner = isNotNilOrEmpty(partner?.id);

  useEffect(() => {
    setShowPanel(!isNil(partner));
  }, [partner]);

  const closePanel = () => {
    setShowPanel(false);
    resetValidationState();
    resetPartner();

    if (onClose) onClose();
  };

  const resetValidationState = () => {
    setNameIsValid(null);
    setCountryIdIsValid(null);
    setDepotsAreValid([]);
  };

  const handleSavePartner = () => {
    resetValidationState();
    let formIsValid = true;

    if (isBlank(partner.name)) {
      setNameIsValid(false);
      formIsValid = false;
    }

    if (isBlank(partner.countryId)) {
      setCountryIdIsValid(false);
      formIsValid = false;
    }

    partner.depots.forEach((depot, index) => {
      depotsAreValid[index] = depot.city !== '';
      setDepotsAreValid(depotsAreValid);

      if (isBlank(depot.city)) {
        formIsValid = false;
      }
    });

    if (formIsValid) {
      savePartner(onClose);
    }
  };

  const getCountriesOptions = () =>
    countries?.map(country => (
      <SelectOption key={country.id} value={country.id}>
        {country.name}
      </SelectOption>
    ));

  const countryStatuses = useMemo(() => {
    return Object.values(BusPartnerStatus).map(status => (
      <SelectOption key={status} value={status}>
        {toCapitalized(status)}
      </SelectOption>
    ));
  }, []);

  const countrySources = useMemo(() => {
    return Object.values(BusPartnerSource).map(source => (
      <SelectOption key={source} value={source}>
        {source}
      </SelectOption>
    ));
  }, []);

  const deletePartnerDepotAction = async index => {
    if (
      await confirm({
        PopupIcon: IconDelete,
        title: 'Delete this depot?',
        message: ``
      })
    ) {
      deletePartnerDepot(index);
    }
  };

  const mobilityPartnerInputId = 'mobility-partner-input-id';

  return (
    <CpcPanel
      title={isEditPartner ? 'Edit partner' : 'Add partner'}
      activeAction={showPanel}
      closeAction={closePanel}
      saveAction={handleSavePartner}
    >
      {!isNil(partner) && !isNil(countries) ? (
        <>
          <ReadOnlyInput
            name={mobilityPartnerInputId}
            id={mobilityPartnerInputId}
            label={
              <LabelWithTooltip
                label="Mobility Partner ID"
                htmlFor={mobilityPartnerInputId}
                tooltipProps={{
                  id: `${mobilityPartnerInputId}-tooltip`,
                  content: fieldInfoContent.mobilityPartnerIdSalesforce,
                  position: 'bottom',
                  size: 'content-fit',
                  className: 'flex-1'
                }}
              />
            }
            value={partner.mobilityPartnerId ?? 'Not available'}
          />
          <div className={`hcr-space-2-top`}>
            <Input
              name="partner-name"
              id="partner-name"
              label="Partner name"
              valid={nameIsValid}
              value={partner.name}
              onChange={e => setPartner({...partner, name: e.target.value})}
            />
          </div>

          <div className={`hcr-space-4-top`}>
            <Select
              id="partner-country"
              label="Country"
              placeholder="Select a Country"
              value={partner.countryId}
              valid={countryIdIsValid}
              onChange={e =>
                setPartner({...partner, countryId: e.target.value})
              }
            >
              {getCountriesOptions()}
            </Select>
          </div>

          <div className={`hcr-space-4-top`}>
            <Select
              id="partner-status"
              label="Status"
              placeholder="Select a Status"
              value={partner.status}
              onChange={e => setPartner({...partner, status: e.target.value})}
              disabled
            >
              {countryStatuses}
            </Select>
          </div>

          <div className={`hcr-space-4-top`}>
            <Select
              id="partner-source"
              label="Source"
              placeholder="Select a Source"
              value={partner.source}
              onChange={e => setPartner({...partner, source: e.target.value})}
              disabled
            >
              {countrySources}
            </Select>
          </div>

          {partner.depots.map((depot, index) => {
            return (
              <div
                key={`partner-depot-${index}`}
                className={`hcr-space-4-top`}
                style={{borderTop: '1px solid #E1E1E1'}}
              >
                <div className={`partner-depot-header hcr-space-2-top`}>
                  <div>Depot {index + 1}</div>
                  <div>
                    <Button
                      display="square"
                      aria-label="Delete Partner Depot"
                      appearance="link"
                      onClick={() => deletePartnerDepotAction(index)}
                    >
                      <Icon InlineIcon={IconDelete} aria-hidden="true" />
                    </Button>
                  </div>
                </div>
                <Grid>
                  <GridCol size={12}>
                    <div className={`hcr-space-2-top`}>
                      <Input
                        name="partner-depot-street"
                        id="partner-depot-street"
                        label="Street + No."
                        value={depot.street}
                        onChange={e =>
                          updatePartnerDepot(index, {
                            ...depot,
                            street: e.target.value
                          })
                        }
                      />
                    </div>
                  </GridCol>
                </Grid>
                <Grid>
                  <GridCol size={6}>
                    <div className={`hcr-space-4-top`}>
                      <Input
                        name="partner-depot-zip-code"
                        id="partner-depot-zip-code"
                        label="ZIP code"
                        value={depot.postCode}
                        onChange={e =>
                          updatePartnerDepot(index, {
                            ...depot,
                            postCode: e.target.value
                          })
                        }
                      />
                    </div>
                  </GridCol>
                  <GridCol size={6}>
                    <div className={`hcr-space-4-top`}>
                      <Input
                        name="partner-depot-city"
                        id="partner-depot-city"
                        label="City"
                        value={depot.city}
                        valid={
                          isEmpty(depotsAreValid) ? null : depotsAreValid[index]
                        }
                        onChange={e =>
                          updatePartnerDepot(index, {
                            ...depot,
                            city: e.target.value
                          })
                        }
                      />
                    </div>
                  </GridCol>
                </Grid>
              </div>
            );
          })}

          <div className={`hcr-space-4-top`}>
            <Button onClick={() => addPartnerDepot()}>
              <Icon InlineIcon={IconOfferSolid} aria-hidden="true" />
              Add depot
            </Button>
          </div>
        </>
      ) : (
        <ContentLoader />
      )}
    </CpcPanel>
  );
};

export default connect(
  state => ({
    partner: state.partners.partner
  }),
  {
    setPartner,
    resetPartner,
    savePartner,
    addPartnerDepot,
    updatePartnerDepot,
    deletePartnerDepot
  }
)(PartnerPanel);
